import { useState } from 'react';

/**
 * Custom hook for accordion functionality
 * Handles state management for accordion items (open/closed states)
 * Supports both single and multiple item modes
 */
const useAccordion = (items, allowMultiple = false) => {
  // Extract always open indexes from items
  const alwaysOpenIndexes = items.reduce((acc, item, index) => {
    if (item.alwaysOpen) acc.push(index);
    return acc;
  }, []);

  // Extract default open items from items
  const defaultOpenItems = items.reduce((acc, item, index) => {
    if (item.isOpen || item.state) acc.push(index);
    return acc;
  }, []);

  // Combine always open and default open items for initial state
  const initialOpenItems = [...defaultOpenItems, ...alwaysOpenIndexes];
  const [openItems, setOpenItems] = useState(initialOpenItems);

  /**
   * Handles toggling of accordion items
   * @param {number} index - Index of the item to toggle
   */
  const handleToggle = (index) => {
    // Prevent toggling of always open items
    if (items[index]?.alwaysOpen) return;

    if (allowMultiple) {
      // Multiple items can be open at once
      setOpenItems((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
    } else {
      // Only one item can be open at a time (plus always open items)
      setOpenItems((prev) => (prev.includes(index) ? alwaysOpenIndexes : [index, ...alwaysOpenIndexes]));
    }
  };

  /**
   * Checks if an item is currently open
   * @param {number} index - Index of the item to check
   * @returns {boolean} - Whether the item is open
   */
  const isItemOpen = (index) => openItems.includes(index);

  /**
   * Gets the current open items array
   * @returns {number[]} - Array of open item indexes
   */
  const getOpenItems = () => openItems;

  /**
   * Manually set open items (useful for external control)
   * @param {number[]} indexes - Array of indexes to set as open
   */
  const setOpenItemsManually = (indexes) => {
    setOpenItems([...indexes, ...alwaysOpenIndexes]);
  };

  return {
    openItems,
    handleToggle,
    isItemOpen,
    getOpenItems,
    setOpenItemsManually,
    alwaysOpenIndexes,
  };
};

export { useAccordion };
